import React, { useEffect, useState } from 'react'
import { Outlet, createFileRoute, redirect, useNavigate, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { SidebarProvider, useSidebar } from '@/context/SidebarContext'
import { useAuthStore } from '@/hooks/auth/useAuthStore'
import { useNavigation } from '@/hooks/useNavigation'
import { useContactNotifications } from '@/hooks/useContactNotifications'
import { useInventoryAlerts } from '@/hooks/useInventoryAlerts'
import { useOrderTracking } from '@/hooks/useOrderTracking'
import { useMessagesRealtime } from '../../hooks/useMessagesRealtime'
import { messagesQueryKeys } from '../../hooks/data/useMessages'
import { SERVER_PREFIX, useNotificationStore } from '../../stores/notificationStore'
import { useStaffNotifications } from '@shared/hooks/data/useNotificationsAdmin'
import type { ContactReceivedPayload } from '@/api/common/ChatHubService'
import type { LowStockPayload } from '@/api/common/InventoryHub'
import type { MessageReceivedPayload } from '@/api/common/MessagesHub'
import type { OrderStatusChangedPayload } from '@/api/common/OrderHub'
import AppHeader from '@/layout/AppHeader'
import AppSidebar from '@/layout/AppSidebar'
import Backdrop from '@/layout/Backdrop'
import LoadingSpinner from '@shared/ui/LoadingSpinner'
import { toast } from '@/ui/Toast'
import type { Role } from '@/types/auth'
import { decodeUser } from '@/api/jwt'
import { ROUTES, getRouteConfig, routeVisibleFor } from '@/config/routes'
import { useMyPermissions } from '@shared/hooks/data/usePermissionMatrix'

/** Synchronous read of the persisted token — beforeLoad runs before React mounts, so this
 * can't go through useAuthStore()/useAuth() hooks. Must match tokenStore.ts's real storage
 * key ('sv-tokens'); the previous 'auth-storage' key was never written by anything, which
 * meant this guard always saw a logged-out user and redirected every request to /signin. */
function readAuthStorage(): { token: string | null; roles: Role[]; isPlatformAdmin: boolean } {
  if (typeof window === 'undefined') return { token: null, roles: [], isPlatformAdmin: false }
  try {
    const raw = localStorage.getItem('sv-tokens')
    const accessToken = raw ? (JSON.parse(raw)?.accessToken ?? null) : null
    if (!accessToken) return { token: null, roles: [], isPlatformAdmin: false }
    const user = decodeUser(accessToken)
    return { token: accessToken, roles: user?.roles ?? [], isPlatformAdmin: user?.isPlatformAdmin === true }
  } catch {
    return { token: null, roles: [], isPlatformAdmin: false }
  }
}

/**
 * Live "new contact" toast + list-invalidation. Every route inside this (admin)
 * layout already requires the 'admin' or 'manager' role (see beforeLoad below —
 * there is no lower-privilege route in this group), but we still gate explicitly
 * on hasRole so the SignalR connection only opens for the roles this feature is
 * meant for, and so it stays correct if a lower-privilege admin route is ever added.
 */
function ContactNotificationsBridge() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.add)

  useContactNotifications((payload: ContactReceivedPayload) => {
    const excerpt = typeof payload.messageExcerpt === 'string' ? payload.messageExcerpt : undefined
    const body = payload.subject ?? excerpt
    toast.info(`New contact request from ${payload.name ?? 'someone'}`, body)
    addNotification({
      kind: 'contact',
      title: `New contact request from ${payload.name ?? 'someone'}`,
      body,
      link: ROUTES.CONTACTS.path,
    })
    queryClient.invalidateQueries({ queryKey: ['contacts'] })
  })

  return null
}

/** Live low-stock toast — same rationale as ContactNotificationsBridge above: gated to
 * admin/manager explicitly rather than relying solely on this layout group's own guard. */
function InventoryAlertsBridge() {
  const addNotification = useNotificationStore((s) => s.add)

  useInventoryAlerts((payload: LowStockPayload) => {
    const body = `${payload.quantityAfter} left (threshold ${payload.minStockLevel}) — SKU ${payload.sku}`
    toast.warning(`Low stock: ${payload.name}`, body)
    addNotification({
      kind: 'low-stock',
      title: `Low stock: ${payload.name}`,
      body,
      link: ROUTES.INVENTORY.path,
    })
  })

  return null
}

/** Live "new support message" toast + cache invalidation — same rationale as the two
 * bridges above. Only toasts on customer-authored messages (a staff reply echoed back
 * to this same connection shouldn't self-notify the sender). Invalidates both the inbox
 * list and that specific conversation's history so an already-open thread picks up the
 * new message without a second SignalR connection in the page itself. */
function MessagesBridge() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.add)

  useMessagesRealtime((payload: MessageReceivedPayload) => {
    if (payload.isFromStaff) return
    toast.info('New support message', payload.body)
    addNotification({
      kind: 'message',
      title: 'New support message',
      body: payload.body,
      link: ROUTES.MESSAGES.path,
    })
    queryClient.invalidateQueries({ queryKey: messagesQueryKeys.inbox })
    queryClient.invalidateQueries({ queryKey: messagesQueryKeys.history(payload.conversationId) })
  })

  return null
}

/** Live order-status toast + cache invalidation — same rationale as the bridges above.
 * Invalidates the orders list (any filter combination) plus that specific order's detail +
 * history sub-keys, so an already-open order detail page (which shows its own Timeline)
 * picks up the change without a second SignalR connection in the page itself. */
function OrderTrackingBridge() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.add)

  useOrderTracking((payload: OrderStatusChangedPayload) => {
    const body = `${payload.previousStatus} → ${payload.newStatus}${payload.note ? ` — ${payload.note}` : ''}`
    toast.info(`Order ${payload.orderNumber} updated`, body)
    addNotification({
      kind: 'order',
      title: `Order ${payload.orderNumber} updated`,
      body,
      link: ROUTES.ORDERS.path,
    })
    queryClient.invalidateQueries({ queryKey: ['orders', 'list'] })
    queryClient.invalidateQueries({ queryKey: ['orders', 'detail', payload.orderId] })
  })

  return null
}

/** Server-side staff notifications — a new order, and every Stripe outcome (paid / failed / refunded) — from NotificationService. Polled
 * every 15 s and merged into the header bell; anything that appeared since the previous poll also raises a toast (the first poll after
 * page load just fills the bell, so opening the dashboard doesn't replay old alerts). */
function StaffNotificationsBridge() {
  const queryClient = useQueryClient()
  const mergeServer = useNotificationStore((s) => s.mergeServer)
  const { data } = useStaffNotifications()
  const seen = React.useRef<Set<string> | null>(null)

  useEffect(() => {
    if (!data) return
    const known = seen.current
    const fresh = data.items.filter((n) => !n.isRead && known !== null && !known.has(n.id))
    seen.current = new Set(data.items.map((n) => n.id))

    mergeServer(
      data.items.map((n) => ({
        id: `${SERVER_PREFIX}${n.id}`,
        kind: 'order' as const,
        title: n.title,
        body: n.message ?? undefined,
        link: ROUTES.ORDERS.path,
        createdAt: new Date(n.createdAt).getTime(),
        read: n.isRead,
      })),
    )

    for (const n of fresh) {
      const failed = n.kind === 'payment-failed'
      ;(failed ? toast.warning : toast.info)(n.title, n.message ?? undefined)
    }
    if (fresh.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] })
    }
  }, [data, mergeServer, queryClient])

  return null
}

/** Sends the person to /403 when the Permissions page has switched this page's module off for their role (or never granted it to their
 * custom role). Sits next to the router's static role check, which knows nothing about the matrix. Admin is exempt. */
function PermissionGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isAdmin = !!user?.roles.includes('admin')
  const { data: cells, isLoading } = useMyPermissions(!!user && !isAdmin)
  const role = isAdmin ? 'admin' : user?.roles.includes('manager') ? 'manager' : user?.roles.find((r) => !['worker', 'customer'].includes(r)) ?? user?.roles[0]
  const route = getRouteConfig(pathname)
  const customRole = !!role && !['admin', 'manager', 'worker', 'customer'].includes(role)
  const allowed = !route || route.key === 'FORBIDDEN' || route.public || isAdmin || !cells ? true : routeVisibleFor(route, role, cells)

  useEffect(() => {
    if (!allowed) navigate({ to: ROUTES.FORBIDDEN.path })
  }, [allowed, navigate])

  // A custom role has nothing until the matrix says so — don't flash the page while that loads.
  if (customRole && isLoading) return <LoadingSpinner />
  return allowed ? <>{children}</> : null
}

function AdminLayoutShell() {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar()
  const canSeeContactNotifications = useAuthStore((s) => s.hasRole(['admin', 'manager']))

  const mainContentMargin = isMobileOpen
    ? 'ml-0'
    : isExpanded || isHovered
      ? 'lg:ml-[290px]'
      : 'lg:ml-[90px]'

  return (
    <div className="min-h-screen">
      {canSeeContactNotifications ? <ContactNotificationsBridge /> : null}
      {canSeeContactNotifications ? <InventoryAlertsBridge /> : null}
      {canSeeContactNotifications ? <MessagesBridge /> : null}
      {canSeeContactNotifications ? <OrderTrackingBridge /> : null}
      {canSeeContactNotifications ? <StaffNotificationsBridge /> : null}
      <AppSidebar />
      <Backdrop />

      <div className={`flex-1 transition-all duration-300 ${mainContentMargin}`}>
        <AppHeader />
        <main className="p-4 mx-auto max-w-(--breakpoint-2xl) md:p-6">
          <PermissionGate>
            <Outlet />
          </PermissionGate>
        </main>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)')({
  beforeLoad: ({ location }) => {
    const { token, roles, isPlatformAdmin } = readAuthStorage()

    if (!token) {
      throw redirect({ to: ROUTES.SIGNIN.path, search: { redirect: location.pathname } })
    }

    // Not logged-in-required routes (e.g. this group has none today) fall through; every
    // other route here must have the current role in its `roles` list — this is the real,
    // router-level enforcement that the old effect-based AuthGuard never actually provided
    // (it only checked isLoggedIn, and was barely wired up anywhere). 'admin' is the
    // super-user role and bypasses per-route role lists, matching useAuthStore's hasRole().
    // isPlatformAdmin bypasses too, same shape but a genuinely separate claim (not a role in
    // this list at all) — its own routes' real enforcement is each route's own beforeLoad
    // (e.g. platform/stores/page.tsx), this is just so this parent guard doesn't 403 a real
    // platform admin before that guard ever runs (PLATFORM_STORES.roles only lists ADMIN so
    // getRouteConfig resolves an exact match — see that route's own doc comment).
    const routeConfig = getRouteConfig(location.pathname)
    const isAdmin = roles.includes('admin')
    // A custom role isn't in any route's static list — its access is decided by the permission matrix in <PermissionGate>.
    const hasCustomRole = roles.some((r) => !['admin', 'manager', 'worker', 'customer'].includes(r))
    if (
      !hasCustomRole &&
      routeConfig &&
      routeConfig.key !== 'FORBIDDEN' &&
      !routeConfig.public &&
      !isAdmin &&
      !isPlatformAdmin &&
      !routeConfig.roles?.some((r) => roles.includes(r as Role))
    ) {
      throw redirect({ to: ROUTES.FORBIDDEN.path })
    }
  },
  component: () => (
    <SidebarProvider>
      <AdminLayoutShell />
    </SidebarProvider>
  ),
})


